import React from 'react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { fetchMoveWorkTypes, timeEntryJobName, type TimeEntry, type TimeJob, type TimeJobChoice, type TimeMoveAccess } from '@/data/scheduleTimeTracking';
import { ModalActionError } from '@/ui/ModalActionError';
import { safeErrorMessage } from '@/utils/appErrors';
import TimeJobFinder, { timeJobKindLabel, timeRangeLabel } from './TimeJobFinder';
import './TimeEntryJobField.css';

dayjs.extend(utc);
dayjs.extend(timezone);

const isChoice = (job: TimeJob | TimeJobChoice): job is TimeJobChoice => 'group' in job;

/** What a save does to the schedule, the job notes and the work type, in the order it happens. */
function moveConsequences(entry: TimeEntry, target: TimeJob | TimeJobChoice, access: TimeMoveAccess, timeZone: string,
  times: { clockIn: string | null; clockOut: string | null }): string[] {
  const first = entry.person_name.split(' ')[0] || entry.person_name;
  const finished = Boolean(times.clockOut);
  const lines: string[] = [];
  if (entry.clock_out && entry.shift_id && entry.planned_starts_at) {
    lines.push(`${entry.job_code} gets its planned shift back (${timeRangeLabel(entry.planned_starts_at, entry.planned_ends_at, timeZone)}).`);
  } else if (entry.clock_out && entry.shift_id) {
    lines.push(`The recorded shift on ${entry.job_code} moves with the time.`);
  }
  if (!finished) {
    lines.push(access === 'self' ? `Your clock keeps running on ${target.code}.` : `${first}’s clock keeps running on ${target.code}.`);
  } else {
    const start = times.clockIn ? Date.parse(times.clockIn) : NaN;
    const end = Date.parse(times.clockOut!);
    const planned = isChoice(target) && target.group === 'mine' && target.starts_at
      && start < Date.parse(target.ends_at ?? dayjs(target.starts_at).add(1, 'hour').toISOString()) && end > Date.parse(target.starts_at);
    lines.push(planned
      ? `This time takes ${first}’s planned shift on ${target.code} (${timeRangeLabel((target as TimeJobChoice).starts_at, (target as TimeJobChoice).ends_at, timeZone)}).`
      : `${first} has no planned shift on ${target.code} at these times, so the schedule shows a recorded shift instead.`);
    lines.push(`Any clock-out note moves to ${target.code}’s Notes tab.`);
  }
  if (entry.job_kind === 'installation') lines.push(`${entry.job_code}’s work type doesn’t carry over.`);
  return lines;
}

/**
 * The entry's job, with Change job to move the time to the job it belongs to. Choosing a job shows what the save
 * will do; an installation also needs its work type. Undo puts the original job back.
 */
export default function TimeEntryJobField({ entry, access, locationId, authorizationKey, timeZone, target, onTarget, workType, onWorkType,
  times, busy, error, workTypeError, startOpen = false }: {
  entry: TimeEntry; access: TimeMoveAccess; locationId: string; authorizationKey: string; timeZone: string;
  target: TimeJob | TimeJobChoice | null; onTarget: (job: TimeJob | TimeJobChoice | null) => void;
  workType: string; onWorkType: (value: string) => void;
  times: { clockIn: string | null; clockOut: string | null }; busy: boolean; error?: string; workTypeError?: string; startOpen?: boolean;
}) {
  const id = React.useId();
  const [picking, setPicking] = React.useState(startOpen);
  const finderInput = React.useRef<HTMLInputElement | null>(null);
  const undoButton = React.useRef<HTMLButtonElement>(null);
  const changeButton = React.useRef<HTMLButtonElement>(null);
  const focusAfter = React.useRef<'finder' | 'undo' | 'change' | null>(startOpen ? 'finder' : null);
  React.useEffect(() => {
    const next = focusAfter.current;
    focusAfter.current = null;
    if (next === 'finder') finderInput.current?.focus();
    else if (next === 'undo') undoButton.current?.focus();
    else if (next === 'change') changeButton.current?.focus();
  }, [picking, target]);
  const installationTarget = target?.kind === 'installation' ? target : null;
  const workTypes = useQuery({
    queryKey: ['schedule-time-move-work-types', authorizationKey, entry.entry_id, installationTarget?.id],
    queryFn: () => fetchMoveWorkTypes(entry.entry_id, installationTarget!.id),
    enabled: Boolean(installationTarget), staleTime: 60_000,
  });
  // The suggestion (the person's planned visit purpose that day, else General) fills an untouched choice.
  const suggested = workTypes.data?.suggested;
  React.useEffect(() => { if (installationTarget && suggested && !workType) onWorkType(suggested); }, [installationTarget, suggested, workType, onWorkType]);
  const shown = target ?? { kind: entry.job_kind, code: entry.job_code, name: timeEntryJobName(entry), status: '', ticket_type: null };
  const kind = target ? timeJobKindLabel(target) : entry.job_kind === 'installation' ? 'Installation' : 'Service';
  const status = target?.status ? target.status.replace(/_/g, ' ') : '';
  return <section className="time-job-field" aria-labelledby={`${id}-title`}>
    <h3 id={`${id}-title`} className="time-job-field__title">Job</h3>
    <div className={`time-job-field__card${target ? ' is-moved' : ''}`}>
      <div className="time-job-field__job">
        <span className="time-job-field__meta"><span className={`time-job-field__kind time-job-field__kind--${shown.kind}`}>{kind}</span>{status ? <span>{status}</span> : null}</span>
        <strong className="time-job-field__name">{shown.code} · {shown.name}</strong>
        {target ? <span className="time-job-field__from">Moved from <s>{entry.job_code} · {timeEntryJobName(entry)}</s></span> : null}
      </div>
      {target ? <button ref={undoButton} type="button" className="btn btn-outline" disabled={busy}
        onClick={() => { focusAfter.current = 'change'; onWorkType(''); onTarget(null); }}>Undo</button>
        : <button ref={changeButton} type="button" className="btn btn-outline time-job-field__change" disabled={busy} aria-expanded={picking} aria-controls={`${id}-finder`}
          onClick={() => { focusAfter.current = picking ? 'change' : 'finder'; setPicking(!picking); }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M7 4 3 8l4 4" /><path d="M3 8h14" /><path d="m17 20 4-4-4-4" /><path d="M21 16H7" /></svg>
          {picking ? 'Keep this job' : 'Change job'}</button>}
    </div>
    {picking && !target ? <div id={`${id}-finder`} className="time-job-field__finder">
      <TimeJobFinder id={`${id}-search`} entry={entry} access={access} locationId={locationId} authorizationKey={authorizationKey} timeZone={timeZone}
        inputRef={finderInput} disabled={busy} onChoose={job => { focusAfter.current = 'undo'; setPicking(false); onWorkType(''); onTarget(job); }} />
      {error ? <span className="schedule-time__error" role="alert">{error}</span> : null}
    </div> : null}
    {target ? <div className="time-job-field__notice" role="status">
      <strong>When you save</strong>
      <ul>{moveConsequences(entry, target, access, timeZone, times).map(line => <li key={line}>{line}</li>)}</ul>
    </div> : null}
    {installationTarget ? <div className="time-job-field__work-type">
      <label htmlFor={`${id}-work-type`}>Work type on {installationTarget.code}</label>
      <select id={`${id}-work-type`} className="input" value={workType} disabled={busy || workTypes.isPending} onChange={event => onWorkType(event.target.value)}
        aria-invalid={Boolean(workTypeError)} aria-describedby={workTypeError ? `${id}-work-type-error` : undefined}>
        {workTypes.isPending ? <option value="">Loading work types…</option> : null}
        {(workTypes.data?.work_types ?? []).map(choice => <option key={choice} value={choice}>{choice}</option>)}
      </select>
      {workTypeError ? <span className="schedule-time__error" id={`${id}-work-type-error`}>{workTypeError}</span> : null}
      <ModalActionError message={workTypes.isError ? safeErrorMessage(workTypes.error, 'Work types could not be loaded.') : null}
        actionLabel="Retry work types" onAction={() => { void workTypes.refetch(); }} />
    </div> : null}
  </section>;
}
